import type { UseQueryResult } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { NOT_IN_SNAPSHOT, type ApiError } from '../lib/api.ts';
import { useT } from '../lib/i18n.tsx';
import { Skeleton } from './ui/skeleton.tsx';
import { EmptyState, ErrorState } from './States.tsx';

/**
 * Renders one query's three states: a skeleton while loading, an error with retry (and a clear
 * "no permission" message for 403), and the data. A failing widget never takes the page down.
 */
export function Async<T>({
  query,
  children,
  skeleton,
}: {
  query: UseQueryResult<T, ApiError>;
  children: (data: T) => ReactNode;
  skeleton?: ReactNode;
}) {
  const t = useT();
  if (query.data !== undefined) return <>{children(query.data)}</>;
  if (query.error) {
    if (query.error.problem?.type === NOT_IN_SNAPSHOT)
      return <EmptyState title={t.demo.notInSnapshotTitle} description={t.demo.notInSnapshot} />;
    if (query.error.status === 403)
      return <EmptyState title={t.errors.forbiddenTitle} description={t.errors.forbiddenDetail} />;
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }
  return (
    <div aria-busy="true" aria-label={t.common.loading}>
      {skeleton ?? <Skeleton className="h-40 w-full" />}
    </div>
  );
}
